/**
 * Funding Blueprint Landing Page
 * ClickFunnels-style single long funnel page
 */

import React, { useState, useEffect } from "react";
import { 
  ArrowRight, 
  CheckCircle, 
  Target,
  TrendingUp,
  Shield,
  Clock,
  CreditCard,
  FileText,
  Users,
  Zap,
  ChevronDown,
  Star,
  AlertCircle,
  XCircle,
  ArrowDown,
  Lock,
  Gift
} from "lucide-react";

const testimonials = [
  {
    name: "Marcus T.",
    business: "Home Remodeling",
    result: "$87,000 in 0% credit",
    quote: "I was skeptical at first, but the blueprint showed me exactly which cards to apply for. Got approved for 6 cards in 3 weeks!",
    rating: 5
  },
  {
    name: "Sarah K.",
    business: "E-commerce",
    result: "$124,000 in funding",
    quote: "The phased approach was genius. I didn't get a single denial and my credit score only dropped 12 points.",
    rating: 5
  },
  {
    name: "David R.",
    business: "Landscaping",
    result: "$63,000 at 0% APR",
    quote: "Finally got the working capital I needed without giving up equity or paying crazy interest rates.",
    rating: 5
  }
];

const problems = [
  "You apply for business credit and get DENIED",
  "Too many inquiries are killing your credit score",
  "You don't know which lenders to apply to first",
  "Banks want collateral you don't have",
  "High interest rates are eating your profits",
  "You're stuck in the endless cycle of getting rejected"
];

const blueprintIncludes = [
  { title: "Complete Credit Profile Analysis", desc: "We analyze your current credit situation to find hidden opportunities" },
  { title: "Personalized Lender List", desc: "Exact cards to apply for based on YOUR profile, not generic advice" },
  { title: "Phased Application Timeline", desc: "Which bureau each lender pulls, and when to apply to minimize impact" },
  { title: "Approval Odds Calculator", desc: "Know your chances BEFORE you apply" },
  { title: "Same-Day Stacking Strategy", desc: "How to submit multiple apps strategically on the same day" },
  { title: "Reconsideration Scripts", desc: "Word-for-word scripts if you get denied (turn NO into YES)" },
  { title: "Step-by-Step Action Plan", desc: "Exact dates and actions — just follow the checklist" },
  { title: "Funding Projections", desc: "See your best, worst, and realistic funding scenarios" }
];

function FAQItem({ q, a, isOpen, onClick }) {
  return (
    <div className="border-b-2 border-gray-200">
      <button onClick={onClick} className="w-full py-6 flex items-center justify-between text-left">
        <span className="font-bold text-xl text-gray-800 pr-8">{q}</span>
        <ChevronDown size={24} className={`text-[#48d480] flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && <p className="pb-6 text-gray-600 text-lg leading-relaxed">{a}</p>}
    </div>
  );
}

export default function FundingBlueprint() {
  const [openFaq, setOpenFaq] = useState(0);
  const [timeLeft, setTimeLeft] = useState({ hours: 23, minutes: 47, seconds: 33 });
  
  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Countdown timer
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        let { hours, minutes, seconds } = prev;
        seconds--;
        if (seconds < 0) { seconds = 59; minutes--; }
        if (minutes < 0) { minutes = 59; hours--; }
        if (hours < 0) { hours = 23; minutes = 59; seconds = 59; }
        return { hours, minutes, seconds };
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, []);

  const faqs = [
    { q: "What exactly is the Business Funding Blueprint?", a: "It's a personalized, step-by-step strategy document that shows you exactly which business credit cards and lines of credit to apply for, in what order, and when — all customized to YOUR specific credit profile and business. No more guessing." },
    { q: "How much funding can I realistically get?", a: "Most of our clients secure between $50,000 to $150,000 in 0% APR business credit within 30 days. Some have gotten over $200K. It depends on your credit profile, but we'll show you exactly what's possible for YOU." },
    { q: "Will this hurt my credit score?", a: "Our phased approach is specifically designed to MINIMIZE inquiry impact. By strategically timing applications across different credit bureaus, we help protect your score while maximizing approvals. Most clients see only a 10-15 point temporary dip." },
    { q: "How is this different from other programs?", a: "Unlike generic courses or 'credit stacking' programs, your Blueprint is 100% personalized to YOUR credit profile, YOUR business, and YOUR situation. We tell you the exact lenders, exact timing, and exact strategy for YOU." },
    { q: "What if I get denied?", a: "Your Blueprint includes word-for-word reconsideration scripts and strategies. We show you exactly what to say to turn a denial into an approval. Plus, our phased approach minimizes denials in the first place." }
  ];

  const scrollToCTA = () => {
    document.getElementById('get-blueprint').scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white" data-testid="funding-blueprint-page">
      
      {/* Sticky Header */}
      <div className="fixed top-0 left-0 right-0 bg-[#0f2d52] text-white py-3 z-50 shadow-lg">
        <div className="max-w-4xl mx-auto px-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Zap size={18} className="text-[#48d480]" />
            <span className="text-sm font-medium">Special Offer Expires In:</span>
            <span className="font-bold text-[#48d480]">
              {String(timeLeft.hours).padStart(2, '0')}:{String(timeLeft.minutes).padStart(2, '0')}:{String(timeLeft.seconds).padStart(2, '0')}
            </span>
          </div>
          <button onClick={scrollToCTA} className="bg-[#48d480] hover:bg-[#3ab370] text-white text-sm font-bold px-4 py-2 rounded transition-colors">
            Get Blueprint →
          </button>
        </div>
      </div>

      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-b from-[#0f2d52] to-[#1a3a5c]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          
          {/* Pre-headline */}
          <p className="text-[#48d480] font-semibold text-lg mb-4 uppercase tracking-wide">
            Attention Business Owners Who Need Funding Fast
          </p>
          
          {/* Main Headline */}
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white leading-tight mb-6">
            Get Up To <span className="text-[#48d480]">$150,000</span> In 
            <span className="text-[#48d480]"> 0% APR</span> Business Credit
            <span className="block mt-2">In The Next 30 Days</span>
          </h1>
          
          {/* Sub-headline */}
          <p className="text-xl md:text-2xl text-white/80 mb-8 max-w-3xl mx-auto leading-relaxed">
            ...Without Getting Denied, Without Destroying Your Credit Score, 
            And Without Begging Banks For A Loan
          </p>
          
          {/* CTA Button */}
          <button 
            onClick={scrollToCTA}
            className="bg-[#48d480] hover:bg-[#3ab370] text-white font-bold text-xl px-12 py-5 rounded-lg transition-all transform hover:scale-105 shadow-2xl shadow-green-900/40 mb-8"
          >
            YES! Give Me My Personalized Blueprint →
          </button>
          
          {/* Trust Elements */}
          <div className="flex flex-wrap justify-center gap-6 text-white/60 text-sm">
            <span className="flex items-center gap-2"><Shield size={16} className="text-[#48d480]" /> No Credit Check</span>
            <span className="flex items-center gap-2"><Lock size={16} className="text-[#48d480]" /> 100% Secure</span>
            <span className="flex items-center gap-2"><Clock size={16} className="text-[#48d480]" /> Instant Access</span>
          </div>
          
          {/* Arrow */}
          <div className="mt-12 animate-bounce">
            <ArrowDown size={32} className="text-white/40 mx-auto" />
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-black text-center text-gray-800 mb-4">
            Are You Tired Of This?
          </h2>
          <p className="text-center text-gray-600 text-lg mb-10">
            If any of these sound familiar, keep reading...
          </p>
          
          <div className="space-y-4">
            {problems.map((problem, idx) => (
              <div key={idx} className="flex items-center gap-4 bg-white p-5 rounded-lg shadow-sm border-l-4 border-red-500">
                <XCircle size={24} className="text-red-500 flex-shrink-0" />
                <span className="text-gray-700 text-lg">{problem}</span>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <p className="text-2xl font-bold text-gray-800 mb-4">
              It doesn't have to be this way.
            </p>
            <p className="text-lg text-gray-600">
              There's a <span className="text-[#48d480] font-bold">proven system</span> to get approved for 
              massive amounts of 0% business credit — if you know exactly what to do.
            </p>
          </div>
        </div>
      </section>

      {/* Solution Intro */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <p className="text-[#48d480] font-bold text-lg mb-4">INTRODUCING</p>
          <h2 className="text-4xl md:text-5xl font-black text-[#0f2d52] mb-6">
            The Business Funding Blueprint
          </h2>
          <p className="text-xl text-gray-600 leading-relaxed mb-8">
            A <span className="font-bold">personalized, step-by-step strategy</span> that shows you 
            <span className="text-[#0f2d52] font-bold"> exactly which credit cards to apply for, 
            in what order, and when</span> — based on YOUR specific credit profile.
          </p>
          
          <div className="bg-[#0f2d52] rounded-2xl p-8 text-white">
            <p className="text-lg mb-6">This isn't generic advice. Your Blueprint includes:</p>
            <div className="grid md:grid-cols-2 gap-4 text-left">
              <div className="flex items-center gap-3">
                <CheckCircle size={20} className="text-[#48d480] flex-shrink-0" />
                <span>The EXACT lenders to apply to</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle size={20} className="text-[#48d480] flex-shrink-0" />
                <span>Which credit bureau each pulls</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle size={20} className="text-[#48d480] flex-shrink-0" />
                <span>Your approval odds for EACH card</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle size={20} className="text-[#48d480] flex-shrink-0" />
                <span>The EXACT order to apply</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle size={20} className="text-[#48d480] flex-shrink-0" />
                <span>When to submit each application</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle size={20} className="text-[#48d480] flex-shrink-0" />
                <span>Scripts if you get denied</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-black text-center text-[#0f2d52] mb-4">
            Here's How It Works
          </h2>
          <p className="text-center text-gray-600 text-lg mb-12">
            Our 3-phase approach maximizes approvals while protecting your credit
          </p>
          
          <div className="space-y-6">
            {[
              { phase: "PHASE 1", week: "Week 1", title: "Equifax Lenders", desc: "Start with lenders that pull Equifax. Lock in your first approvals while inquiries are fresh on only ONE bureau." },
              { phase: "PHASE 2", week: "Week 2", title: "TransUnion Lenders", desc: "Move to TransUnion-pulling lenders. Your Equifax inquiries won't affect these applications." },
              { phase: "PHASE 3", week: "Week 4", title: "Experian Lenders", desc: "Complete your funding stack with Experian lenders. By now, you've maximized approvals across all bureaus." }
            ].map((step, idx) => (
              <div key={idx} className="bg-white rounded-xl p-6 shadow-lg flex gap-6 items-start">
                <div className="w-16 h-16 rounded-full bg-[#48d480] text-white font-black text-2xl flex items-center justify-center flex-shrink-0">
                  {idx + 1}
                </div>
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-[#48d480] font-bold text-sm">{step.phase}</span>
                    <span className="text-gray-400">•</span>
                    <span className="text-gray-500 text-sm">{step.week}</span>
                  </div>
                  <h3 className="text-xl font-bold text-[#0f2d52] mb-2">{step.title}</h3>
                  <p className="text-gray-600">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-black text-center text-[#0f2d52] mb-4">
            What's Inside Your Blueprint
          </h2>
          <p className="text-center text-gray-600 text-lg mb-12">
            Everything you need to stack $50K-$150K+ in business credit
          </p>
          
          <div className="space-y-4">
            {blueprintIncludes.map((item, idx) => (
              <div key={idx} className="flex gap-4 p-5 bg-gray-50 rounded-xl">
                <div className="w-8 h-8 rounded-full bg-[#48d480] text-white font-bold flex items-center justify-center flex-shrink-0 text-sm">
                  {idx + 1}
                </div>
                <div>
                  <h4 className="font-bold text-[#0f2d52] text-lg">{item.title}</h4>
                  <p className="text-gray-600">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 bg-[#0f2d52]">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-black text-center text-white mb-4">
            Real Results From Real Business Owners
          </h2>
          <p className="text-center text-white/70 text-lg mb-12">
            Join hundreds who've already unlocked their funding potential
          </p>
          
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t, idx) => (
              <div key={idx} className="bg-white rounded-xl p-6">
                <div className="flex gap-1 mb-4">
                  {[...Array(t.rating)].map((_, i) => (
                    <Star key={i} size={18} className="text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-600 mb-4 italic">"{t.quote}"</p>
                <div className="border-t pt-4">
                  <p className="font-bold text-[#0f2d52]">{t.name}</p>
                  <p className="text-gray-500 text-sm">{t.business}</p>
                  <p className="text-[#48d480] font-bold mt-1">{t.result}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="get-blueprint" className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-2xl mx-auto px-4 text-center">
          
          {/* Urgency */}
          <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 mb-8">
            <p className="text-red-600 font-bold flex items-center justify-center gap-2">
              <AlertCircle size={20} />
              Special Pricing Ends When Timer Hits Zero
            </p>
          </div>
          
          <h2 className="text-3xl md:text-4xl font-black text-[#0f2d52] mb-6">
            Get Your Personalized Blueprint Now
          </h2>
          
          {/* Pricing Box */}
          <div className="bg-white rounded-2xl shadow-2xl border-4 border-[#48d480] p-8 mb-8">
            <p className="text-gray-500 line-through text-xl mb-2">Regular Price: $497</p>
            <p className="text-5xl font-black text-[#0f2d52] mb-2">$97</p>
            <p className="text-[#48d480] font-bold mb-6">One-Time Payment • Instant Access</p>
            
            <a
              href="https://app.trulli.ai/marketplace/legacytriangle"
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full bg-[#48d480] hover:bg-[#3ab370] text-white font-bold text-xl py-5 rounded-lg transition-all transform hover:scale-105 shadow-lg mb-6"
            >
              GET MY BLUEPRINT NOW →
            </a>
            
            <div className="flex flex-wrap justify-center gap-4 text-sm text-gray-500">
              <span className="flex items-center gap-1"><Lock size={14} /> Secure Checkout</span>
              <span className="flex items-center gap-1"><Shield size={14} /> 30-Day Guarantee</span>
            </div>
          </div>
          
          {/* Guarantee */}
          <div className="bg-[#0f2d52] rounded-xl p-6 text-white text-left">
            <div className="flex gap-4">
              <Gift size={40} className="text-[#48d480] flex-shrink-0" />
              <div>
                <h4 className="font-bold text-lg mb-2">100% Money-Back Guarantee</h4>
                <p className="text-white/80 text-sm">
                  If you follow your Blueprint and don't get approved for at least ONE credit card within 30 days, 
                  we'll refund every penny. No questions asked.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-black text-center text-[#0f2d52] mb-12">
            Frequently Asked Questions
          </h2>
          <div>
            {faqs.map((faq, idx) => (
              <FAQItem
                key={idx}
                q={faq.q}
                a={faq.a}
                isOpen={openFaq === idx}
                onClick={() => setOpenFaq(openFaq === idx ? -1 : idx)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-[#0f2d52]">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-black text-white mb-6">
            Stop Guessing. Start Stacking.
          </h2>
          <p className="text-white/70 text-lg mb-8">
            Your personalized funding strategy is just one click away.
          </p>
          <a
            href="https://app.trulli.ai/marketplace/legacytriangle"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-[#48d480] hover:bg-[#3ab370] text-white font-bold text-xl px-12 py-5 rounded-lg transition-all transform hover:scale-105 shadow-2xl"
          >
            GET MY BLUEPRINT NOW →
          </a>
          <p className="text-white/50 text-sm mt-6">
            © {new Date().getFullYear()} Legacy Triangle. All rights reserved.
          </p>
        </div>
      </section>
      
    </div>
  );
}
